import { useState, useCallback, useRef } from 'react';
import { mergeDefined } from '../utils/positionIdentity';
import { normalizeBrokerPosition } from '../utils/brokerNumericFormat';

function symbolKey(symbol) {
    if (symbol == null || symbol === '') return null;
    const key = String(symbol).trim().toUpperCase();
    return key || null;
}

function symbolFromPositionKey(positionKey) {
    if (typeof positionKey !== 'string') return null;
    const idx = positionKey.indexOf('::');
    if (idx === -1) return null;
    return symbolKey(positionKey.slice(idx + 2));
}

/** Quantity the payload itself carried. Missing quantity is not zero and not a reopen. */
function explicitQuantity(position) {
    if (!position || position.Quantity == null || position.Quantity === '') return null;
    const qty = parseFloat(position.Quantity);
    return Number.isFinite(qty) ? qty : null;
}

function isExplicitlyFlat(position) {
    if (!position) return false;
    if (position.Deleted === true || position.deleted === true) return true;
    const qty = explicitQuantity(position);
    return qty != null && Math.abs(qty) === 0;
}

/**
 * Centralized position state manager.
 * Handles all position updates, deduplication, and merging logic in one place.
 * Deduplicates by PositionKey (AccountID::Symbol).
 * 
 * @returns {Object} Position manager API
 */
export function usePositionsManager() {
    const [positions, setPositionsInternal] = useState([]);
    const prevAccountIdRef = useRef(null);
    // Symbols the position stream has closed. A stale REST snapshot or a later
    // price-only tick must not put the row back. Cleared on a real reopen (explicit qty > 0).
    const closedSymbolsRef = useRef(new Set());

    const buildPositionKey = useCallback((position) => {
        const accountId = position?.AccountID;
        const symbol = position?.Symbol;
        if (!accountId || !symbol) return null;
        return `${String(accountId)}::${String(symbol)}`;
    }, []);

    // Canonical identity for UI state: one row per (AccountID, Symbol).
    // Preserve upstream broker PositionID separately for traceability.
    const normalizePositionIdentity = useCallback((position) => {
        if (!position) return position;
        const positionKey = buildPositionKey(position);
        if (!positionKey) return normalizeBrokerPosition(position);
        const sourcePositionId =
            position.SourcePositionID ??
            position.PositionID ??
            null;
        return normalizeBrokerPosition({
            ...position,
            PositionKey: positionKey,
            SourcePositionID: sourcePositionId,
            // Backward-compatible field for existing UI consumers.
            PositionID: position.PositionID ?? positionKey,
        });
    }, [buildPositionKey]);

    /**
     * Internal helper: Deduplicate positions by PositionKey.
     * When duplicates exist, merge them using mergeDefined.
     */
    const deduplicatePositions = useCallback((positionsList) => {
        if (!Array.isArray(positionsList) || positionsList.length === 0) {
            return [];
        }

        const byId = new Map();

        for (const position of positionsList) {
            const normalizedPosition = normalizePositionIdentity(position);
            const id = normalizedPosition?.PositionKey;
            if (!id) {
                console.warn('[PositionsManager] Skipping position without PositionKey:', position);
                continue;
            }

            const existing = byId.get(id);
            if (existing) {
                // Merge duplicate into existing
                byId.set(id, mergeDefined(existing, normalizedPosition));
            } else {
                byId.set(id, normalizedPosition);
            }
        }

        return Array.from(byId.values());
    }, [normalizePositionIdentity]);

    /**
     * Replace all positions (used for initial load or account switch).
     * Automatically deduplicates.
     */
    const replacePositions = useCallback((newPositions) => {
        const closed = closedSymbolsRef.current;
        const rows = deduplicatePositions(newPositions).filter(
            (p) => !closed.has(symbolKey(p?.Symbol))
        );
        setPositionsInternal(rows);
    }, [deduplicatePositions]);

    /**
     * Merge positions from API query with existing stream data.
     * Prefers query data but preserves stream-only fields.
     */
    const mergeQueryPositions = useCallback((queryPositions) => {
        setPositionsInternal(prev => {
            if (!Array.isArray(queryPositions)) return prev;

            // Empty REST snapshot = no open positions for this account
            if (queryPositions.length === 0) {
                return [];
            }

            const closed = closedSymbolsRef.current;
            const withoutClosed = queryPositions.filter((p) => !closed.has(symbolKey(p?.Symbol)));
            const openRows = withoutClosed.filter((p) => !isExplicitlyFlat(p));

            // Snapshot is only flat rows (qty 0 / Deleted) — the book is flat.
            if (withoutClosed.length > 0 && openRows.length === 0 && withoutClosed.every(isExplicitlyFlat)) {
                return [];
            }
            // Snapshot is only rows the stream already closed. Do not resurrect them,
            // and do not wipe other symbols that the stream still has open.
            if (openRows.length === 0) {
                return prev.filter((p) => !closed.has(symbolKey(p?.Symbol)));
            }

            const accountIds = new Set(
                openRows.map((p) => p?.AccountID).filter(Boolean)
            );

            // Drop leftovers from other accounts when REST returns a scoped snapshot
            const scopedPrev = accountIds.size > 0
                ? (Array.isArray(prev) ? prev.filter((p) => accountIds.has(p?.AccountID)) : [])
                : (Array.isArray(prev) ? prev : []);

            if (scopedPrev.length === 0) {
                return deduplicatePositions(openRows);
            }

            const byId = new Map();

            // REST snapshot is authoritative for this account — start from query rows,
            // then merge any newer stream fields already present in prev for those IDs.
            for (const p of openRows) {
                const id = normalizePositionIdentity(p)?.PositionKey;
                if (!id) continue;
                byId.set(id, normalizePositionIdentity(p));
            }

            for (const p of scopedPrev) {
                const id = p?.PositionKey;
                if (!id || !byId.has(id)) continue;
                byId.set(id, mergeDefined(p, byId.get(id)));
            }

            return deduplicatePositions(Array.from(byId.values()));
        });
    }, [deduplicatePositions]);

    /**
     * Update or add a single position from stream.
     * CRITICAL: Only adds new positions when PositionKey can be resolved.
     */
    const updatePosition = useCallback((positionData) => {
        const normalizedPosition = normalizePositionIdentity(positionData);
        const positionKey = normalizedPosition?.PositionKey;
        
        if (!positionKey) {
            console.warn('[PositionsManager] Ignoring position update without PositionKey:', positionData);
            return;
        }

        const qty = explicitQuantity(normalizedPosition);
        const sym = symbolKey(normalizedPosition.Symbol);
        const explicitOpen = qty != null && Math.abs(qty) > 0;
        if (sym && closedSymbolsRef.current.has(sym)) {
            if (!explicitOpen) return;
            closedSymbolsRef.current.delete(sym);
        }

        setPositionsInternal(prev => {
            let existingIndex = prev.findIndex(p => p.PositionKey === positionKey);
            if (existingIndex === -1 && sym) {
                const symbolIndexes = [];
                prev.forEach((p, i) => {
                    if (symbolKey(p?.Symbol) === sym) symbolIndexes.push(i);
                });
                if (symbolIndexes.length === 1) existingIndex = symbolIndexes[0];
            }

            if (existingIndex !== -1) {
                // Update existing position - merge new data
                const updated = [...prev];
                updated[existingIndex] = mergeDefined(updated[existingIndex], normalizedPosition);
                return deduplicatePositions(updated);
            }
            // A price-only tick must not create a row. Opens carry an explicit quantity.
            if (!explicitOpen) return prev;
            const updated = [...prev, normalizedPosition];
            return deduplicatePositions(updated);
        });
    }, [deduplicatePositions, normalizePositionIdentity]);

    /**
     * Remove a position by PositionKey (or resolvable account/symbol pair).
     */
    const removePosition = useCallback((positionKeyOrData) => {
        // Support PositionKey string or position-like object.
        const positionKey = typeof positionKeyOrData === 'string'
            ? positionKeyOrData
            : (
                positionKeyOrData?.PositionKey ||
                buildPositionKey(positionKeyOrData) ||
                null
            );
        const sym = symbolKey(
            typeof positionKeyOrData === 'string'
                ? null
                : positionKeyOrData?.Symbol
        ) || symbolFromPositionKey(positionKey);
        // Tombstone before the state update so a snapshot merged in the same turn cannot restore it.
        if (sym) closedSymbolsRef.current.add(sym);
        if (!positionKey && !sym) return;

        setPositionsInternal(prev => prev.filter((p) => {
            if (positionKey && p.PositionKey === positionKey) return false;
            if (sym && symbolKey(p?.Symbol) === sym) return false;
            return true;
        }));
    }, [buildPositionKey]);

    /**
     * Clear all positions (used for account switch).
     */
    const clearPositions = useCallback(() => {
        closedSymbolsRef.current.clear();
        setPositionsInternal([]);
    }, []);

    /**
     * Clear positions only when account actually changes.
     */
    const clearPositionsOnAccountChange = useCallback((accountId) => {
        const prevAccountId = prevAccountIdRef.current;
        
        // Only clear if account ID changed (not on initial mount)
        if (prevAccountId !== undefined && prevAccountId !== null && accountId !== prevAccountId) {
            console.log('🧹 [PositionsManager] Clearing positions - account switched from', prevAccountId, 'to', accountId);
            clearPositions();
        }
        
        prevAccountIdRef.current = accountId;
    }, [clearPositions]);

    return {
        // State
        positions,
        
        // Actions
        replacePositions,      // Replace all positions (initial load)
        mergeQueryPositions,   // Merge API query results
        updatePosition,        // Update single position from stream
        removePosition,        // Remove position (for deletions)
        clearPositions,        // Clear all positions
        clearPositionsOnAccountChange, // Smart clear on account switch
    };
}
